import {
  Table,
  Empty,
  Spin,
  Typography,
  Space,
  Tag,
  Tooltip,
  Button,
  Popconfirm,
  Modal,
  Form,
  Input,
  InputNumber,
  Select,
  Switch,
  Dropdown,
  Grid,
} from "antd";
import type { MenuProps } from "antd";

const { useBreakpoint } = Grid;
import {
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  SelectOutlined,
  DownOutlined,
} from "@ant-design/icons";
import { CreateRelatedModal } from "@/components/CreateRelatedModal";
import { useTranslate, useNavigation } from "@refinedev/core";
import { useRelationshipGridColumns } from "@/hooks/useRelationshipGridColumns";
import { useListValues } from "@/hooks/useListValues";
import { ItemPickerModal, type PickedItem } from "@/components/ItemPickerModal";
import {
  usePendingRelationships,
  type PendingRelationship,
} from "@/contexts/PendingRelationshipsContext";
import type { RelationshipMetadata, RelationshipGridColumn } from "@/types";
import { useState, useMemo } from "react";

const { Text, Link } = Typography;

/**
 * Convert snake_case to camelCase
 * Aras property names use snake_case, but our API returns camelCase
 */
const snakeToCamel = (str: string): string => {
  return str.replace(/_([a-z])/g, (_, letter) => letter.toUpperCase());
};

interface PendingRelationshipTableProps {
  /** The source ItemType name */
  itemType: string;
  /** The relationship metadata */
  relationship: RelationshipMetadata;
  /** Whether this tab is currently active */
  isActive: boolean;
}

/**
 * Table component for displaying pending relationship items in Create mode.
 * Similar to RelationshipTable but works with PendingRelationshipsContext
 * instead of making API calls.
 */
export const PendingRelationshipTable: React.FC<
  PendingRelationshipTableProps
> = ({ itemType: _itemType, relationship, isActive }) => {
  const translate = useTranslate();
  const { show } = useNavigation();
  const screens = useBreakpoint();
  const isMobile = !screens.md;
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isPickerOpen, setIsPickerOpen] = useState(false);
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [editingItem, setEditingItem] = useState<PendingRelationship | null>(
    null
  );
  const [selectedItem, setSelectedItem] = useState<PickedItem | null>(null);
  const [form] = Form.useForm();

  // Get pending relationships from context
  const {
    getPendingByRelationshipName,
    addPendingRelationship,
    updatePendingRelationship,
    removePendingRelationship,
  } = usePendingRelationships();

  // Get pending items for this relationship
  const pendingItems = useMemo(
    () => getPendingByRelationshipName(relationship.name),
    [getPendingByRelationshipName, relationship.name]
  );

  // Fetch grid column metadata from API
  const { data: gridColumnsData, isLoading: gridColumnsLoading } =
    useRelationshipGridColumns({
      relationshipName: relationship.name,
      enabled: isActive,
    });

  // Extract list names from grid columns for list value translation
  const listColumnNames = useMemo(() => {
    if (!gridColumnsData?.columns) return [];
    return gridColumnsData.columns
      .filter(
        (col) =>
          (col.dataType === "list" || col.dataType === "filter list") &&
          col.dataSource
      )
      .map((col) => col.dataSource as string);
  }, [gridColumnsData?.columns]);

  // Get editable columns with full metadata for form rendering
  const editableColumns = useMemo(() => {
    if (!gridColumnsData?.columns) return [];
    return gridColumnsData.columns.filter(
      (col) => col.source === "relationship" && !col.isReadOnly
    );
  }, [gridColumnsData?.columns]);

  // Fetch list values for list-type columns
  const { lists: listValues, isLoading: listsLoading } = useListValues({
    listNames: listColumnNames,
    enabled: isActive && listColumnNames.length > 0,
  });

  // Handle "Add Existing" - open the item picker first
  const handleAddExisting = () => {
    setEditingItem(null);
    setSelectedItem(null);
    form.resetFields();
    if (relationship.relatedItemTypeName) {
      setIsPickerOpen(true);
    } else {
      setIsModalOpen(true);
    }
  };

  // Handle "Create New" - open the create modal
  const handleCreateNew = () => {
    setIsCreateModalOpen(true);
  };

  // Handle created item from CreateRelatedModal
  const handleItemCreated = (
    newItemId: string,
    newItemData: Record<string, unknown>
  ) => {
    setIsCreateModalOpen(false);
    setSelectedItem({
      id: newItemId,
      keyedName:
        (newItemData.name as string) ||
        (newItemData.keyedName as string) ||
        newItemId,
      ...newItemData,
    });
    setIsModalOpen(true);
  };

  // Dropdown menu items for Add button
  const addMenuItems: MenuProps["items"] = [
    {
      key: "addExisting",
      label: translate("buttons.addExisting", "Add Existing"),
      icon: <SelectOutlined />,
      onClick: handleAddExisting,
    },
    {
      key: "createNew",
      label: translate("buttons.createNew", "Create New"),
      icon: <PlusOutlined />,
      onClick: handleCreateNew,
    },
  ];

  // Handle item selection from picker (supports multi-select)
  const handleItemSelect = (items: PickedItem[]) => {
    if (items.length === 0) return;

    setIsPickerOpen(false);

    if (items.length === 1) {
      // Single item: open properties modal as before
      setSelectedItem(items[0]);
      setIsModalOpen(true);
    } else {
      // Multiple items: add all as pending relationships without properties modal
      for (const item of items) {
        addPendingRelationship(
          relationship.name,
          item.id,
          item as Record<string, unknown>,
        );
      }
    }
  };

  // Handle Edit button click
  const handleEdit = (record: PendingRelationship) => {
    setEditingItem(record);
    form.setFieldsValue(record.properties);
    setIsModalOpen(true);
  };

  // Handle Delete button click
  const handleDelete = (record: PendingRelationship) => {
    removePendingRelationship(record.id);
  };

  // Handle modal form submission
  const handleModalSubmit = async () => {
    try {
      const values = await form.validateFields();

      if (editingItem) {
        // Update existing pending item
        updatePendingRelationship(editingItem.id, {
          properties: values,
        });
      } else {
        // Add new pending item
        addPendingRelationship(
          relationship.name,
          selectedItem?.id,
          selectedItem as Record<string, unknown> | undefined,
          values
        );
      }

      setIsModalOpen(false);
      setSelectedItem(null);
      setEditingItem(null);
      form.resetFields();
    } catch {
      // Form validation error - do nothing
    }
  };

  // Handle modal cancel
  const handleModalCancel = () => {
    setIsModalOpen(false);
    setSelectedItem(null);
    setEditingItem(null);
    form.resetFields();
  };

  // Build columns from metadata
  const columns = useMemo(() => {
    type TableColumn = {
      title: string | React.ReactNode;
      dataIndex?: string[];
      key: string;
      width?: number;
      align?: "left" | "center" | "right";
      render?: (value: unknown, record: PendingRelationship) => React.ReactNode;
    };

    // Helper function to create render function based on data type
    const createRenderer = (
      col: RelationshipGridColumn
    ):
      | ((value: unknown, record: PendingRelationship) => React.ReactNode)
      | undefined => {
      const propName = snakeToCamel(col.name);

      // Special handling for name/keyed_name columns - make them clickable links
      if (
        col.source === "related" &&
        (propName === "name" || propName === "keyedName")
      ) {
        return (_value: unknown, record: PendingRelationship) => {
          const relatedTypeName = relationship.relatedItemTypeName;
          const displayValue =
            record.relatedItemData?.[propName] ||
            record.relatedItemData?.["keyedName"] ||
            record.relatedItemData?.["name"] ||
            record.relatedId;
          if (relatedTypeName && record.relatedId) {
            return (
              <Link onClick={() => show(relatedTypeName, record.relatedId!)}>
                {String(displayValue || "")}
              </Link>
            );
          }
          return <Text>{String(displayValue || "")}</Text>;
        };
      }

      // State with tag
      if (propName === "state" || propName === "stateLabel") {
        return (_value: unknown, record: PendingRelationship) => {
          const value = record.relatedItemData?.[propName];
          if (!value) return null;
          return <Tag>{String(value)}</Tag>;
        };
      }

      // Date formatting
      if (col.dataType === "date" || col.dataType === "datetime") {
        return (_value: unknown, record: PendingRelationship) => {
          const value =
            col.source === "related"
              ? record.relatedItemData?.[propName]
              : record.properties[propName];
          if (!value) return null;
          return new Date(String(value)).toLocaleDateString();
        };
      }

      // Boolean formatting
      if (col.dataType === "boolean") {
        return (_value: unknown, record: PendingRelationship) => {
          const value =
            col.source === "related"
              ? record.relatedItemData?.[propName]
              : record.properties[propName];
          if (value === null || value === undefined) return null;
          const checked =
            value === true || value === "1" || value === 1 || value === "true";
          return checked ? "Yes" : "No";
        };
      }

      // List value translation
      if (
        (col.dataType === "list" || col.dataType === "filter list") &&
        col.dataSource
      ) {
        return (_value: unknown, record: PendingRelationship) => {
          const value =
            col.source === "related"
              ? record.relatedItemData?.[propName]
              : record.properties[propName];
          if (!value) return null;
          const rawValue = String(value);
          const listVals = listValues[col.dataSource!];
          if (listVals) {
            const listItem = listVals.find((v) => v.value === rawValue);
            if (listItem?.label) {
              return (
                <Tooltip title={listItem.label}>
                  <Text
                    ellipsis
                    style={{ maxWidth: col.width ? col.width - 20 : 100 }}
                  >
                    {listItem.label}
                  </Text>
                </Tooltip>
              );
            }
          }
          return <Text>{rawValue}</Text>;
        };
      }

      // Long text with tooltip
      if (
        propName === "comments" ||
        propName === "description" ||
        col.dataType === "text"
      ) {
        return (_value: unknown, record: PendingRelationship) => {
          const value =
            col.source === "related"
              ? record.relatedItemData?.[propName]
              : record.properties[propName];
          return (
            <Tooltip title={String(value || "")}>
              <Text
                ellipsis
                style={{ maxWidth: col.width ? col.width - 20 : 180 }}
              >
                {String(value || "")}
              </Text>
            </Tooltip>
          );
        };
      }

      return undefined;
    };

    // Build columns from metadata
    const buildColumnsFromMetadata = (
      gridColumns: RelationshipGridColumn[]
    ): TableColumn[] => {
      const cols: TableColumn[] = [];

      for (const col of gridColumns) {
        const propName = snakeToCamel(col.name);
        const customRenderer = createRenderer(col);

        // Create render function that extracts value from correct location
        const render = (
          _value: unknown,
          record: PendingRelationship
        ): React.ReactNode => {
          const value =
            col.source === "related"
              ? record.relatedItemData?.[propName]
              : record.properties[propName];

          if (customRenderer) {
            return customRenderer(value, record);
          }

          if (value === undefined || value === null || value === "") {
            return null;
          }
          return <Text>{String(value)}</Text>;
        };

        cols.push({
          title: col.label,
          key: col.key,
          width: col.width,
          align: col.alignment as "left" | "center" | "right",
          render,
        });
      }

      return cols;
    };

    // Build fallback columns from data
    const buildColumnsFromData = (): TableColumn[] => {
      if (pendingItems.length === 0) {
        return [
          {
            title: translate("fields.name", "Name"),
            key: "name",
            render: (_: unknown, record: PendingRelationship) => {
              const displayName =
                record.relatedItemData?.keyedName ||
                record.relatedItemData?.name ||
                record.relatedId ||
                "—";
              return <Text>{String(displayName)}</Text>;
            },
          },
        ];
      }

      const cols: TableColumn[] = [];

      // Add name/keyed_name column
      cols.push({
        title: translate("fields.name", "Name"),
        key: "name",
        render: (_: unknown, record: PendingRelationship) => {
          const displayName =
            record.relatedItemData?.keyedName ||
            record.relatedItemData?.name ||
            record.relatedId;
          const relatedTypeName = relationship.relatedItemTypeName;
          if (relatedTypeName && record.relatedId) {
            return (
              <Link onClick={() => show(relatedTypeName, record.relatedId!)}>
                {String(displayName || "")}
              </Link>
            );
          }
          return <Text>{String(displayName || "")}</Text>;
        },
      });

      // Add item_number if available
      if (pendingItems[0]?.relatedItemData?.itemNumber !== undefined) {
        cols.push({
          title: translate("fields.itemNumber", "Item Number"),
          key: "itemNumber",
          width: 150,
          render: (_: unknown, record: PendingRelationship) => {
            return (
              <Text>{String(record.relatedItemData?.itemNumber || "")}</Text>
            );
          },
        });
      }

      // Add state if available
      if (pendingItems[0]?.relatedItemData?.state !== undefined) {
        cols.push({
          title: translate("fields.state", "State"),
          key: "state",
          width: 120,
          render: (_: unknown, record: PendingRelationship) => {
            const state = record.relatedItemData?.state;
            if (!state) return null;
            return <Tag>{String(state)}</Tag>;
          },
        });
      }

      return cols;
    };

    // Build data columns: prefer metadata, fallback to data analysis
    let dataColumns: TableColumn[];
    if (gridColumnsData?.columns && gridColumnsData.columns.length > 0) {
      dataColumns = buildColumnsFromMetadata(gridColumnsData.columns);
    } else {
      dataColumns = buildColumnsFromData();
    }

    // Always add Actions column at the end
    const actionsWidth = isMobile ? 70 : 90;

    const actionsColumn: TableColumn = {
      title: translate("table.actions", "Actions"),
      key: "actions",
      width: actionsWidth,
      align: "center",
      render: (_: unknown, record: PendingRelationship) => {
        return (
          <Space size="small">
            <Tooltip title={translate("buttons.edit", "Edit")}>
              <Button
                type="text"
                size="small"
                icon={<EditOutlined />}
                onClick={() => handleEdit(record)}
              />
            </Tooltip>
            <Popconfirm
              title={translate(
                "buttons.confirm",
                "Are you sure you want to delete this item?"
              )}
              onConfirm={() => handleDelete(record)}
              okText={translate("buttons.yes", "Yes")}
              cancelText={translate("buttons.no", "No")}
            >
              <Tooltip title={translate("buttons.delete", "Delete")}>
                <Button
                  type="text"
                  size="small"
                  danger
                  icon={<DeleteOutlined />}
                />
              </Tooltip>
            </Popconfirm>
          </Space>
        );
      },
    };

    return [...dataColumns, actionsColumn];
  }, [
    gridColumnsData,
    listValues,
    relationship,
    translate,
    show,
    pendingItems,
    isMobile,
  ]);

  if (gridColumnsLoading || listsLoading) {
    return (
      <div style={{ textAlign: "center", padding: "40px" }}>
        <Spin size="large" />
      </div>
    );
  }

  return (
    <>
      <div
        style={{
          display: "flex",
          justifyContent: "flex-end",
          marginBottom: isMobile ? 12 : 16,
        }}
      >
        {relationship.relatedItemTypeName ? (
          <Dropdown.Button
            type="primary"
            icon={<DownOutlined />}
            menu={{ items: addMenuItems }}
            onClick={handleAddExisting}
          >
            <PlusOutlined /> {translate("buttons.add", "Add")}
          </Dropdown.Button>
        ) : (
          <Button
            type="primary"
            icon={<PlusOutlined />}
            onClick={handleAddExisting}
          >
            {translate("buttons.add", "Add")}
          </Button>
        )}
      </div>

      {pendingItems.length === 0 ? (
        <Empty
          description={translate("common.noItems", "No items found")}
          image={Empty.PRESENTED_IMAGE_SIMPLE}
        />
      ) : (
        <Table
          dataSource={pendingItems}
          columns={columns}
          rowKey="id"
          size="small"
          scroll={{ x: "max-content" }}
          pagination={false} // No pagination for pending items
        />
      )}

      {/* Item Picker Modal */}
      {relationship.relatedItemTypeName && (
        <ItemPickerModal
          itemTypeName={relationship.relatedItemTypeName}
          open={isPickerOpen}
          onCancel={() => setIsPickerOpen(false)}
          onSelect={handleItemSelect}
          multiSelect
        />
      )}

      {/* Add/Edit Properties Modal */}
      <Modal
        title={
          editingItem
            ? translate("buttons.edit", "Edit") +
              " " +
              (relationship.tabLabel || relationship.name)
            : translate("buttons.add", "Add") +
              " " +
              (relationship.tabLabel || relationship.name)
        }
        open={isModalOpen}
        onOk={handleModalSubmit}
        onCancel={handleModalCancel}
      >
        <Form form={form} layout="vertical">
          {/* Display selected item (for new relationships) */}
          {!editingItem && selectedItem && (
            <Form.Item label={translate("fields.relatedItem", "Related Item")}>
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  padding: "8px 12px",
                  border: "1px solid #d9d9d9",
                  borderRadius: 6,
                  backgroundColor: "#fafafa",
                }}
              >
                <Space>
                  <SelectOutlined />
                  <Text strong>
                    {selectedItem.keyedName ||
                      selectedItem.name ||
                      selectedItem.id}
                  </Text>
                  {selectedItem.itemNumber && (
                    <Text type="secondary">({selectedItem.itemNumber})</Text>
                  )}
                </Space>
                <Button
                  type="link"
                  size="small"
                  onClick={() => {
                    setIsModalOpen(false);
                    setIsPickerOpen(true);
                  }}
                >
                  {translate("buttons.change", "Change")}
                </Button>
              </div>
            </Form.Item>
          )}

          {/* Render all editable fields dynamically from metadata */}
          {editableColumns.map((col) => {
            const fieldName = snakeToCamel(col.name);
            const fieldLabel = col.label;

            // Render different input based on data type
            switch (col.dataType) {
              case "boolean":
                return (
                  <Form.Item
                    key={col.key}
                    name={fieldName}
                    label={fieldLabel}
                    valuePropName="checked"
                  >
                    <Switch />
                  </Form.Item>
                );

              case "integer":
              case "float":
              case "decimal":
                return (
                  <Form.Item key={col.key} name={fieldName} label={fieldLabel}>
                    <InputNumber style={{ width: "100%" }} />
                  </Form.Item>
                );

              case "list":
              case "filter list":
                return (
                  <Form.Item key={col.key} name={fieldName} label={fieldLabel}>
                    <Select
                      allowClear
                      showSearch
                      placeholder={translate("dynamicForm.select", {
                        field: fieldLabel,
                      })}
                      options={
                        col.dataSource && listValues[col.dataSource]
                          ? listValues[col.dataSource].map((opt) => ({
                              label: opt.label,
                              value: opt.value,
                            }))
                          : []
                      }
                      filterOption={(input, option) =>
                        (option?.label?.toString() ?? "")
                          .toLowerCase()
                          .includes(input.toLowerCase())
                      }
                    />
                  </Form.Item>
                );

              case "text":
                return (
                  <Form.Item key={col.key} name={fieldName} label={fieldLabel}>
                    <Input.TextArea rows={3} />
                  </Form.Item>
                );

              default:
                // string and other types
                return (
                  <Form.Item key={col.key} name={fieldName} label={fieldLabel}>
                    <Input />
                  </Form.Item>
                );
            }
          })}

          {/* Message when no editable properties */}
          {editableColumns.length === 0 && !selectedItem && editingItem && (
            <Text type="secondary">
              {translate(
                "messages.noEditableProperties",
                "This relationship has no editable properties."
              )}
            </Text>
          )}
        </Form>
      </Modal>

      {/* Create Related Item Modal */}
      {relationship.relatedItemTypeName && (
        <CreateRelatedModal
          itemTypeName={relationship.relatedItemTypeName}
          open={isCreateModalOpen}
          onCancel={() => setIsCreateModalOpen(false)}
          onCreated={handleItemCreated}
          title={`${translate("buttons.createNew", "Create New")} ${
            relationship.relatedItemTypeName
          }`}
        />
      )}
    </>
  );
};

export default PendingRelationshipTable;
